import { useParams } from "react-router-dom";
import { themeVars } from "../theme";
import { checkoutTemplateFor } from "./index";

/**
 * Static preview harness for the 17 checkout templates — route
 * `/checkout-preview/:id`. Renders the real CheckoutTemplate{N} with mock data
 * (no network) so the admin can capture accurate thumbnails / previews without
 * a wired course. Colours per template mirror the server presets so each
 * preview matches its live design.
 */

// primary / btnText / footerBg per template (mirrors CourseLandingTemplates::PRESETS).
const PRESETS = {
  1: ["#1daa61", "#ffffff", "#0d1117"],
  2: ["#f59e0b", "#1e1b4b", "#0f0d1a"],
  3: ["#00bba7", "#009e8d", "#1e2530"],
  4: ["#c9a84c", "#17150e", "#17150e"],
  5: ["#b5f23d", "#0a0a0a", "#0a0a0a"],
  6: ["#6d5efc", "#ffffff", "#eef1f8"],
  7: ["#f43f5e", "#ffffff", "#0a0a10"],
  8: ["#6366f1", "#ffffff", "#15162f"],
  9: ["#d4a24e", "#1a1208", "#0c0b10"],
  10: ["#c2683f", "#ffffff", "#2a2018"],
  11: ["#4f46e5", "#ffffff", "#0f1424"],
  12: ["#8b5cf6", "#ffffff", "#140a1f"],
  13: ["#e11d48", "#ffffff", "#0a0a0a"],
  14: ["#22d3ee", "#06141f", "#060f1d"],
  15: ["#ff6b3d", "#ffffff", "#2a1a14"],
  16: ["#4f7cff", "#ffffff", "#070b1a"],
  17: ["#2ee6a6", "#04110b", "#05080a"],
};

const FIELDS = [
  { name: "first_name", label: "First Name", type: "text", required: true },
  { name: "email", label: "Email ID", type: "email", required: true },
  { name: "phone", label: "Phone Number", type: "tel", required: true },
  { name: "billing_address", label: "Billing Address", type: "text", required: false },
  { name: "city", label: "City", type: "text", required: false },
  { name: "state", label: "State", type: "text", required: false },
];

function mockData(id) {
  const [primary, btnText, footerBg] = PRESETS[id] || PRESETS[1];
  return {
    template_id: id,
    has_template: true,
    primary,
    btnText,
    footerBg,
    course_name: "Full-Stack Web Development",
    community_name: "My Academy",
    logo: null,
    image: null,
    price: 199,
    currency: "USD",
    headline: "",
    sub_headline: "",
    summary_label: "Course enrollment",
    guarantee: "",
    guarantee_status: true,
    product_id: 1,
    purchasable: true,
    fields: FIELDS,
    plans: [
      { pricing_id: 1, name: "One-time", type: "onetimefee", price: 199, setup_fee: 0, currency: "USD", frequency: "", preferred: true },
      { pricing_id: 2, name: "3 monthly payments", type: "subscription", price: 79, subscription_price: 79, setup_fee: 0, currency: "USD", frequency: "month", preferred: false },
    ],
    gateways: [
      { integration_id: 1, api_type: "stripe", label: "Card", image: null },
      { integration_id: 2, api_type: "razorpay", label: "UPI / Netbanking", image: null },
    ],
    coupon_enabled: true,
    bumps: [
      {
        bump_product_id: 1,
        bump_name: "Bonus Templates Pack",
        bump_price: 29,
        bump_option: "0",
        currency: "USD",
        data: { headcontent: "Add the Bonus Templates Pack", subheadcontent: "50 done-for-you templates — one-time offer" },
      },
    ],
    ip: "0.0.0.0",
    trust: {
      secure_text: "256-bit SSL encrypted · your details are secure",
      guarantee_show: true,
      guarantee_title: "30-Day Money-Back Guarantee",
      guarantee_body: "Not satisfied? Get a full refund within 30 days — no questions asked.",
      terms_show: true,
      terms_required: true,
      terms_label: "I agree to the Terms & Conditions and Privacy Policy",
      terms_url: "",
    },
  };
}

export default function CheckoutPreview() {
  const { id } = useParams();
  const data = mockData(Number(id) || 1);
  const Template = checkoutTemplateFor(data.template_id);
  const vars = themeVars({ primary: data.primary, btnText: data.btnText, footerBg: data.footerBg });

  return (
    <div style={vars}>
      <Template data={data} code="preview" />
    </div>
  );
}
